import { createContext, useContext, useMemo, type PropsWithChildren } from "react";

import { useTouchpoints } from "hooks/api/use-touchpoints";
import { useCampaignJobPolling } from "hooks/use-campaign-job-polling";
import { useEmailWriterPollingSessionActive } from "hooks/use-email-writer-polling-session-active";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";

import { TouchpointDTOBatchJobExecutionStatus } from "generated/schemas";

type CampaignJobPollingContextValue = {
  startPolling: () => void;
};

const CampaignJobPollingContext = createContext<CampaignJobPollingContextValue | null>(null);

export const CampaignJobPollingProvider = ({ children }: PropsWithChildren) => {
  const currentCampaign = useCurrentCampaign();
  const currentClient = useCurrentClient();
  const { data } = useTouchpoints({});
  const isEmailWriterSessionActive = useEmailWriterPollingSessionActive(
    currentCampaign.id,
    currentClient.id,
  );

  const hasAnyRunningTouchpoint = useMemo(
    () =>
      data?.items?.some(
        (item) => item.batchJobExecutionStatus === TouchpointDTOBatchJobExecutionStatus.RUNNING,
      ) ?? false,
    [data?.items],
  );

  // Monday job-states polling is for Monday sync only. Email writer / rewrite has its own
  // completion poller; enabling this path during a writer session also invalidates
  // touchpoint list/detail when RUNNING clears.
  const shouldPollJobs = hasAnyRunningTouchpoint && !isEmailWriterSessionActive;

  const { startPolling } = useCampaignJobPolling(shouldPollJobs);

  const value = useMemo(() => ({ startPolling }), [startPolling]);

  return (
    <CampaignJobPollingContext.Provider value={value}>{children}</CampaignJobPollingContext.Provider>
  );
};

export const useCampaignJobPollingContext = (): CampaignJobPollingContextValue => {
  const context = useContext(CampaignJobPollingContext);

  if (!context) {
    throw new Error("useCampaignJobPollingContext must be used within CampaignJobPollingProvider");
  }

  return context;
};
